<script lang="ts" setup>
import { GpTextInput } from '@gportal/uikit';
import { useNodeStore } from 'gpcore-apikit/store/pinia/node';
import AsyncModal from 'gpcore-uikit/components/new/AsyncModal.vue';
import { computed, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    show: boolean;
    /**
     * Called after the node has been destroyed on the backend but before it is removed
     * from the store. Awaited, so the parent can fully navigate away (resolving the
     * router push) before the active node is cleared and the layout collapses.
     */
    onDestroyed?: () => void | Promise<void>;
  }>(),
  {
    show: false,
    onDestroyed: undefined,
  },
);

const emit = defineEmits<{ (e: 'close') }>();

const fqdn = ref('');

const nodeStore = useNodeStore();

const node = computed(() => nodeStore.node);

const isValid = computed(() => node.value.fqdn.toLowerCase() === fqdn.value.toLowerCase());
const destroy = async () => {
  if (isValid.value) {
    // destroyNode performs the API call first (throwing on failure so the modal can
    // surface the error) and only then removes the node from the store. Removing the
    // active node nulls the `node` getter, which collapses the node-detail layout
    // (v-if="node != null") and blanks the screen. We pass an `onDestroyed` hook so the
    // parent navigates away to the node overview *before* the store entry is removed.
    await nodeStore.destroyNode(node.value, {
      onDestroyed: props.onDestroyed,
    });
  }

  return undefined;
};

const onDone = () => {
  emit('close');
};
</script>

<template>
  <AsyncModal
    :show="show"
    :header-icon="['fas', 'fa-fingerprint']"
    :header-text="`Delete node ${node.fqdn}`"
    confirm-text="Delete"
    :is-valid="node.fqdn.toLowerCase() === fqdn.toLowerCase()"
    :async-function="destroy"
    @close="emit('close')"
    @done="onDone"
  >
    <template #content>
      <div class="tw-mb-8 tw-px-4">
        <p class="tw-mb-4">
          Destroying a node is irreversible, all data will be immediately destroyed. All disks will
          be securely wiped during the process.
        </p>
        <GpTextInput
          label="Confirm with node hostname"
          input-id="node-modal-destroy"
          v-model="fqdn"
          name="fqdn"
          :placeholder="node.fqdn"
          transparent
        />
      </div>
    </template>
  </AsyncModal>
</template>
